import {
    BadgeSize,
    ButtonFlavor,
    ButtonType,
    ExButton,
    ExEmptyState,
    ExIconButton,
    ExLoader,
    ExPageHeader,
    ExSideDrawer,
    IconButtonSize,
    IconButtonType,
    LoaderSize,
    TooltipAlignment,
} from '@boomi/exosphere';
import { useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { DetailTask } from '../sources';
import { ApproveRejectDialog } from './ApproveRejectDialog';
import './task-detail.css';
import classNames from 'classnames';
import DateWithTooltip from '../DateWithTooltip';
import TaskStatusBadge from '../TaskStatusBadge';
import TaskTimeline from './timeline/TaskTimeline';

const DELAYED_LOADER_TIMEOUT = 500;

interface Props {
    selectedTask: DetailTask | null;
    loadSelectedTask: (id: string | null) => void;
    isLoadingSelectedTask: boolean;
    approveTask: (id: string, comments: string | null) => void;
    rejectTask: (id: string, comments: string | null) => void;
}

const MOBILE_WIDTH = 1100;

const TaskDetail = ({
    selectedTask,
    loadSelectedTask,
    isLoadingSelectedTask,
    approveTask,
    rejectTask,
}: Props) => {
    const [tooltipText, setTooltipText] = useState('Copy');
    const [approveOrReject, setApproveOrReject] = useState<'approve' | 'reject'>('approve');
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isClosingMobile, setIsClosingMobile] = useState(false);
    const taskId = selectedTask?.id ?? '';
    const timeoutRef = useRef<number | null>(null);
    const loaderTimeoutRef = useRef<number | null>(null);
    const [showDelayedLoader, setShowDelayedLoader] = useState(false);
    const [isTimelineSidebarOpen, setIsTimelineSidebarOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_WIDTH);

    // Handle delayed loader
    useEffect(() => {
        if (isLoadingSelectedTask) {
            loaderTimeoutRef.current = window.setTimeout(() => {
                setShowDelayedLoader(true);
            }, DELAYED_LOADER_TIMEOUT);
        } else {
            clearTimeout(loaderTimeoutRef.current ?? undefined);
            setShowDelayedLoader(false);
        }

        return () => {
            clearTimeout(loaderTimeoutRef.current ?? undefined);
        };
    }, [isLoadingSelectedTask]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= MOBILE_WIDTH);
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleCopy = () => {
        navigator.clipboard.writeText(taskId);
        setTooltipText('Copied');

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = window.setTimeout(() => {
            setTooltipText('Copy');
            timeoutRef.current = null;
        }, 3000);
    };

    const handleOpenApproveOrReject = (mode: 'approve' | 'reject') => {
        setApproveOrReject(mode);
        setIsDialogOpen(true);
    };

    const handleDialogConfirm = (comments: string | null) => {
        if (selectedTask) {
            if (approveOrReject === 'reject') {
                rejectTask(selectedTask.id, comments);
            } else if (approveOrReject === 'approve') {
                approveTask(selectedTask.id, comments);
            }
        }
        setIsDialogOpen(false);
    };

    const handleDialogCancel = () => {
        setIsDialogOpen(false);
    };

    const handleClose = () => {
        if (isMobile) {
            setIsClosingMobile(true);

            setTimeout(() => {
                loadSelectedTask(null);
                setIsClosingMobile(false);
            }, 300);
        } else {
            loadSelectedTask(null);
        }
    };

    const openTimelineSidebar = () => {
        setIsTimelineSidebarOpen(!isTimelineSidebarOpen);
    };

    if (showDelayedLoader) {
        return <ExLoader size={LoaderSize.MEDIUM} />;
    }

    if (selectedTask == null) {
        return (
            <ExEmptyState label="No task selected" text="Select a task to view details">
                <div slot="header">
                    <img src="/img/empty-state.svg" alt="No task selected" />
                </div>
            </ExEmptyState>
        );
    }

    return (
        <>
            <div className={classNames('task-detail-header', isClosingMobile && 'closing')}>
                <ExPageHeader pageTitle={selectedTask.title}>
                    <div slot="body">
                        <div className="task-id">
                            Task ID: {taskId}
                            <ExIconButton
                                icon="Copy"
                                type={IconButtonType.TERTIARY}
                                size={IconButtonSize.SMALL}
                                tooltipText={tooltipText}
                                onClick={handleCopy}
                                hideBrowserTooltip
                            />
                        </div>
                    </div>
                </ExPageHeader>
                <div className="task-detail-header-right">
                    <div className="header-right-row">
                        <DateWithTooltip
                            dateString={selectedTask.updatedAt}
                            alignment={TooltipAlignment.END}
                        />
                        {isMobile && (
                            <ExIconButton
                                icon="Close"
                                type={IconButtonType.TERTIARY}
                                onClick={handleClose}
                            />
                        )}
                    </div>
                    <div className="header-right-row">
                        <TaskStatusBadge status={selectedTask.status} size={BadgeSize.DEFAULT} />
                        <ExIconButton
                            icon="Time"
                            hideBrowserTooltip
                            tooltipText="Timeline"
                            type={IconButtonType.SECONDARY}
                            onClick={openTimelineSidebar}
                            data-testid="timeline-button"
                        />
                    </div>
                </div>
            </div>
            <div className="button-row">
                {selectedTask.canApprove && (
                    <>
                        <ExButton
                            onClick={() => handleOpenApproveOrReject('approve')}
                            data-testid="approve-button"
                        >
                            Approve
                        </ExButton>
                        <ExButton
                            flavor={ButtonFlavor.RISKY}
                            type={ButtonType.SECONDARY}
                            onClick={() => handleOpenApproveOrReject('reject')}
                            data-testid="deny-button"
                        >
                            Deny
                        </ExButton>
                    </>
                )}
            </div>
            <ExSideDrawer
                top={56}
                open={isTimelineSidebarOpen}
                onClose={() => setIsTimelineSidebarOpen(false)}
                panelTitle="Timeline"
                icon="Time"
                noMarginSlot={true}
            >
                <TaskTimeline selectedTask={selectedTask} />
            </ExSideDrawer>
            <div className="task-content">
                <Markdown remarkPlugins={[remarkGfm]}>{selectedTask?.content}</Markdown>
            </div>
            <ApproveRejectDialog
                isOpen={isDialogOpen}
                mode={approveOrReject}
                onCancel={handleDialogCancel}
                onConfirm={handleDialogConfirm}
            />
        </>
    );
};

export default TaskDetail;
